import { useEffect, useState } from 'react';
import {
  loanApplicationNetworkService,
  loanApplicationNetworkServiceInit,
} from '~/services/LoanApplicationNetworkService';
import { ConfigService } from '~/services/ConfigService/ConfigService';
import deviceId from '@sunbit-dev/common/services/deviceId';
import { useTrack } from '~/hooks/track';
import qs from 'qs';
import { Loader } from '@googlemaps/js-api-loader';
import { useHistory, useLocation } from 'react-router-dom';
import URLs, {
  ExternalMessagesRoutes,
  externalMessagesRoutes,
  MessageURLs,
  PageRedirectExternalMessagesRoutes,
  RouteToErrorMapping,
  RouteToUrlMapping,
} from '~/enums/URLs';
import { setIsLoggedIn } from '~/store/reducers/customer/customer';
import { useDispatch } from 'react-redux';
import { addError } from '~/store/reducers/errors/errors';
import { setReferer } from '~/store/reducers/purchase/purchase';
import Storage from '@sunbit-dev/common/services/storage';
import { ApplicationLocalStorageKey } from '~/enums/ApplicationLocalStorageKey';
import { isSlugReferer } from '~/helpers/referer';

export const useApplicationInitializer = () => {
  const [isInitializing, setIsInitializing] = useState(true);
  const { track } = useTrack();
  const history = useHistory();
  const { search } = useLocation();
  const dispatch = useDispatch();

  useEffect(() => {
    (async () => {
      const { token, referer, exvt } = qs.parse(search, { ignoreQueryPrefix: true });

      const shortToken = (token || '') as string;

      let finalReferer = referer as string | undefined;
      if (finalReferer) {
        Storage.setItem(ApplicationLocalStorageKey.REFERER, finalReferer);
        dispatch(setReferer({ referer: finalReferer }));
      } else {
        const savedReferer = Storage.getItem(ApplicationLocalStorageKey.REFERER) as string | null;
        if (savedReferer) {
          finalReferer = savedReferer;
          dispatch(setReferer({ referer: savedReferer }));
        }
      }

      const googleApiLoaderPromise = new Loader({
        apiKey: 'AIzaSyAW5mCzHzBVFCnnJIQThK0UcVRKodtTaLQ',
        libraries: ['places'],
      })?.load();

      loanApplicationNetworkServiceInit({
        baseUrl: ConfigService.config?.apiUrl,
        headers: { deviceId: `loan-application-${deviceId}` },
        track,
        onAuthenticationRequired: () => {
          dispatch(setIsLoggedIn({ isLoggedIn: false }));
        },
      });

      // The /evnoia landing is public and token-less; auth comes from the exvt param in the URL,
      // so skip the shortToken routing that would otherwise redirect it to the invalid-link page.
      // The CDN serves the SPA from '/', so a deep link to /evnoia arrives with its path stripped
      // to '/' (the exvt query is preserved). Detect the flow by the exvt param and client-route to /evnoia.
      if (exvt) {
        if (window.location.pathname !== URLs.Evnoia) {
          history.replace({ pathname: URLs.Evnoia, search });
        }
        setIsInitializing(false);
        return;
      }

      if (!shortToken) {
        if (isSlugReferer(referer as string | undefined)) {
          if (window.location.pathname !== URLs.Intro) {
            history.replace({ pathname: URLs.Intro, search });
          }
        } else {
          history.replace({ pathname: URLs.Message + MessageURLs.InvalidLink, search });
        }
      } else if (externalMessagesRoutes.includes(shortToken as ExternalMessagesRoutes)) {
        if (shortToken in RouteToErrorMapping) {
          const errorMapping = RouteToErrorMapping[shortToken];
          if (errorMapping) {
            dispatch(
              addError({
                eventName: errorMapping.eventName,
                payload: { code: errorMapping.errorCode },
              })
            );
          }
        } else if (shortToken in RouteToUrlMapping) {
          const url = RouteToUrlMapping[shortToken as PageRedirectExternalMessagesRoutes];
          history.replace({ pathname: url, search });
        } else {
          history.replace({ pathname: URLs.Message + MessageURLs.InvalidLink, search });
        }
      } else {
        loanApplicationNetworkService.setShortToken({ shortToken });
        await Promise.all([googleApiLoaderPromise]);
      }
      setIsInitializing(false);
    })();
  }, []);

  return { isInitializing };
};
